import React, { useCallback } from 'react';
import { Control, Path } from 'react-hook-form';
import { defineMessages, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { TUpdateOwnDriverInfoForm } from '@/modules/driver/model/driver';
import { AppDispatch } from '@/root-store';
import { TextField } from '@src-common/view/adapters/reactHookForm/TextField';

import { IRelatedInfoFieldsProps, IRelatedInfoHandle } from './config';
import { useDriverTarget } from './useDriverTarget';
import { useRelatedInfoForm } from './useRelatedInfoForm';

type THomeAddressForm = Pick<TUpdateOwnDriverInfoForm, 'homeAddress'>;

const messages = defineMessages({
  homeAddressTitle: {
    defaultMessage: 'Home address',
    id: '4TxXBI',
    description: 'Field title for address input',
  },
  homeAddressPostcodeHint: {
    defaultMessage: 'Please make sure to include the postcode in your address',
    id: 'upload_document_address_postcode_hint',
    description: 'Upload form: home address input hint',
  },
});

const HomeAddressTextField = <TFieldValues extends THomeAddressForm>({
  control,
}: {
  control: Control<TFieldValues>;
}) => {
  const intl = useIntl();

  return (
    <TextField
      name={'homeAddress' as Path<TFieldValues>}
      title={intl.formatMessage(messages.homeAddressTitle)}
      description={intl.formatMessage(messages.homeAddressPostcodeHint)}
      control={control}
      isRequired={false}
    />
  );
};

const HomeAddressField = React.forwardRef<IRelatedInfoHandle, IRelatedInfoFieldsProps>(
  ({ scope }, ref) => {
    const dispatch = useDispatch() as AppDispatch;

    const { driver, updateDriver } = useDriverTarget(scope);

    const onSubmit = useCallback(
      (formValues: THomeAddressForm) => dispatch(updateDriver(formValues)).unwrap(),
      [dispatch, updateDriver],
    );

    const { control } = useRelatedInfoForm<THomeAddressForm>(ref, {
      defaultValues: {
        homeAddress: driver?.homeAddress ?? '',
      },
      onSubmit,
    });

    return <HomeAddressTextField control={control} />;
  },
);

HomeAddressField.displayName = 'HomeAddressField';

export { HomeAddressField, HomeAddressTextField };
